import  { useState, useMemo, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useCookies } from "react-cookie";
import { CircularProgress } from "@mui/material";
import axios from "axios";
import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Timeline as TimelineIcon,
} from "@mui/icons-material";
import Layout from "../components/layout/CompliceLayout";
import config from "../config.js";

const CompliceRelationshipManagement = () => {
  const [cookies] = useCookies(["userId"]);
  const { API_BASE_URL } = config;

  const [relationships, setRelationships] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      organization: "",
      contactPerson: "",
      relationshipType: "",
      status: "",
      lastInteraction: "",
      nextScheduledInteraction: "",
      keyObjectives: "",
      communicationFrequency: "",
      performanceScore: 0,
      notes: "",
    },
  });

  const filteredRelationships = useMemo(() => {
    return relationships.filter(
      (relationship) =>
        (filter === "" ||
          relationship.organization
            .toLowerCase()
            .includes(filter.toLowerCase()) ||
          relationship.contactPerson
            .toLowerCase()
            .includes(filter.toLowerCase())) &&
        (statusFilter === "" || relationship.status === statusFilter) &&
        (typeFilter === "" || relationship.relationshipType === typeFilter)
    );
  }, [relationships, filter, statusFilter, typeFilter]);

  const fetchRelationships = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/relationships/${cookies.userId}`,
        {
          headers: {
            Authorization: `Bearer ${cookies.userId}`,
          },
        }
      );
      setRelationships(response.data.relationships);
      setError(null);
    } catch (err) {
      setError("Failed to fetch relationships");
      console.error("Error fetching relationships:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (relationship = null) => {
    if (relationship) {
      setSelectedId(relationship._id);
      Object.keys(relationship).forEach((key) => {
        if (key === "keyObjectives") {
          setValue(key, relationship[key].join(", "));
        } else {
          setValue(key, relationship[key]);
        }
      });
    } else {
      reset();
      setSelectedId(null);
    }
    setOpenDialog(true);
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      
      const transformedData = {
        ...data,
        userId: cookies.userId,
        keyObjectives: data.keyObjectives.split(",").map(obj => obj.trim()),
        performanceScore: parseFloat(data.performanceScore)
      };

      if (selectedId) {
        const { _id, ...updateData } = transformedData;
        
        const response = await axios.put(
          `${API_BASE_URL}/api/v1/relationships/${selectedId}`,
          updateData,
          {
            headers: {
              Authorization: `Bearer ${cookies.userId}`,
              "Content-Type": "application/json",
            },
          }
        );
        setRelationships(prevRelationships =>
          prevRelationships.map(r => 
            r._id === selectedId ? response.data.updatedRelationship : r
          )
        );
        setSuccessMessage("Relationship updated successfully");
      } else {
        const response = await axios.post(
          `${API_BASE_URL}/api/v1/relationships`,
          transformedData,
          {
            headers: {
              Authorization: `Bearer ${cookies.userId}`,
              "Content-Type": "application/json",
            },
          }
        );
        setRelationships(prevRelationships => 
          [...prevRelationships, response.data.newRelationship]
        );
        setSuccessMessage("Relationship created successfully");
      }
      setError(null);
      setOpenDialog(false);
    } catch (err) {
      setError(
        `Failed to ${selectedId ? "update" : "create"} relationship: ${
          err.response?.data?.message || err.message
        }`
      );
      console.error("Error saving relationship:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRelationship = async (id) => {
    try {
      setLoading(true);
      await axios.delete(`${API_BASE_URL}/api/v1/relationships/${id}`, {
        headers: {
          Authorization: `Bearer ${cookies.userId}`,
        },
      });
      setRelationships(prevRelationships => 
        prevRelationships.filter(r => r._id !== id)
      );
      setSuccessMessage("Relationship deleted successfully");
      setError(null);
    } catch (err) {
      setError(
        `Failed to delete relationship: ${err.response?.data?.message || err.message}`
      );
      console.error("Error deleting relationship:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRelationships();
  }, []);

  return (
    <Layout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          External Relationship Management
        </Typography>

        {/* Filters */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search Relationships"
              variant="outlined"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <BusinessIcon sx={{ mr: 2, color: "action.active" }} />
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Emerging">Emerging</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Relationship Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Relationship Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="Strategic Partner">Strategic Partner</MenuItem>
                <MenuItem value="Compliance Consultant">
                  Compliance Consultant
                </MenuItem>
                <MenuItem value="Technology Partner">Technology Partner</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add New Relationship
            </Button>
          </Grid>
        </Grid>

        {/* Relationships Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Organization</TableCell>
                <TableCell>Contact Person</TableCell>
                <TableCell>Relationship Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Interaction</TableCell>
                <TableCell>Next Interaction</TableCell>
                <TableCell>Performance</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRelationships.map((relationship) => (
                <TableRow key={relationship._id}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <BusinessIcon sx={{ mr: 2 }} />
                      {relationship.organization}
                    </Box>
                  </TableCell>
                  <TableCell>{relationship.contactPerson}</TableCell>
                  <TableCell>
                    <Chip
                      label={relationship.relationshipType}
                      color={
                        relationship.relationshipType === "Strategic Partner"
                          ? "primary"
                          : relationship.relationshipType ===
                            "Compliance Consultant"
                          ? "secondary"
                          : "default"
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={relationship.status}
                      color={
                        relationship.status === "Active"
                          ? "success"
                          : relationship.status === "Emerging"
                          ? "warning"
                          : "error"
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CalendarIcon sx={{ mr: 1, fontSize: "small" }} />
                      {relationship.lastInteraction}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <TimelineIcon sx={{ mr: 1, fontSize: "small" }} />
                      {relationship.nextScheduledInteraction}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={`Performance Score: ${relationship.performanceScore}/10`}
                    >
                      <Chip
                        label={`${relationship.performanceScore}/10`}
                        color={
                          relationship.performanceScore >= 8
                            ? "success"
                            : relationship.performanceScore >= 5
                            ? "warning"
                            : "error"
                        }
                        size="small"
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog(relationship)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteRelationship(relationship._id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add/Edit Dialog */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogTitle>
              {selectedId ? "Edit Relationship" : "Add New Relationship"}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="organization"
                    control={control}
                    rules={{ required: "Organization is required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Organization"
                        error={!!errors.organization}
                        helperText={errors.organization?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="contactPerson"
                    control={control}
                    rules={{ required: "Contact person is required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Contact Person"
                        error={!!errors.contactPerson}
                        helperText={errors.contactPerson?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="relationshipType"
                    control={control}
                    rules={{ required: "Relationship type is required" }}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.relationshipType}>
                        <InputLabel>Relationship Type</InputLabel>
                        <Select {...field} label="Relationship Type">
                          <MenuItem value="Strategic Partner">
                            Strategic Partner
                          </MenuItem>
                          <MenuItem value="Compliance Consultant">
                            Compliance Consultant
                          </MenuItem>
                          <MenuItem value="Technology Partner">
                            Technology Partner
                          </MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="status"
                    control={control}
                    rules={{ required: "Status is required" }}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.status}>
                        <InputLabel>Status</InputLabel>
                        <Select {...field} label="Status">
                          <MenuItem value="Active">Active</MenuItem>
                          <MenuItem value="Emerging">Emerging</MenuItem>
                          <MenuItem value="Inactive">Inactive</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="lastInteraction"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Last Interaction Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="nextScheduledInteraction"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Next Scheduled Interaction"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="communicationFrequency"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Communication Frequency"
                        placeholder="e.g., Weekly, Monthly"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="performanceScore"
                    control={control}
                    rules={{
                      required: "Performance score is required",
                      min: {
                        value: 0,
                        message: "Score must be at least 0",
                      },
                      max: {
                        value: 10,
                        message: "Score must be at most 10",
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Performance Score"
                        type="number"
                        inputProps={{ min: 0, max: 10, step: 0.1 }}
                        error={!!errors.performanceScore}
                        helperText={errors.performanceScore?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="keyObjectives"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Key Objectives"
                        helperText="Separate objectives with commas"
                        multiline
                        rows={2}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="notes"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        rows={4}
                        label="Additional Notes"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => {
                  setOpenDialog(false);
                  reset();
                }} 
                color="secondary"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={loading}
              >
                {loading ? "Saving..." : selectedId ? "Update" : "Create"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={!!error || !!successMessage}
          autoHideDuration={6000}
          onClose={() => {
            setError(null);
            setSuccessMessage("");
          }}
        >
          <Alert
            severity={error ? "error" : "success"}
            onClose={() => {
              setError(null);
              setSuccessMessage("");
            }}
            sx={{ width: '100%' }}
          >
            {error || successMessage}
          </Alert>
        </Snackbar>

        {/* Loading overlay */}
        {loading && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 9999,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
    </Layout>
  );
};

export default CompliceRelationshipManagement;
